import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { PopulateAndTrackMapRequest } from 'src/app/modules/cop/web-workers/shared/populate-track-map-request.model';
import { TrackFlightLayerChangesRequest } from 'src/app/modules/cop/web-workers/shared/track-flight-layer-changes-request.model';
import { WebWorkerMessageHandlerService } from 'src/app/modules/core/web-workers/web-worker-message-handler.service';
import {
  POPULATE_AND_TRACK_LAYER,
  POPULATE_AND_TRACK_MAP,
  PROCESS_CONNECTION_STATS,
  PROCESS_DATASET_CHANGE_EVENT,
  PROCESS_LAYER_DATA_CHANGE_EVENTS,
  PROCESS_WORKFLOW_CHANGE_EVENT,
  SET_AUTH_TOKEN,
  SET_ENVIRONMENT,
  SET_FLOOR_FILTER_FOR_LAYERS,
  START_TRACK_FLIGHT_LAYER_CHANGES,
  STOP_TRACK_FLIGHT_LAYER_CHANGES,
  SET_MAP_POSITION_AND_ZOOM,
  PROCESS_VMS_DATA_CHANGE_EVENT,
  OPEN_VMS_CONNECTION,
  CLOSE_VMS_CONNECTION,
  SEND_VMS_REQUEST,
  PROCESS_TEMPORARY_LAYER_TRACKING
} from 'src/app/modules/cop/web-workers/shared/web-worker-messages';
import { LoggerService } from 'src/app/modules/core/services/logger/logger.service';

import { SignalRConnectionStatsService } from '../../services/signalr-connection-stats-service';
import { PopulateAndTrackLayerRequest } from '../shared/populate-track-layer-request.model';
import { ConnectionStatsHandler } from './handlers/connections-stats.handler';
import { ProcessDatasetChangeEventHandler } from './handlers/process-dataset-change-event.handler';
import { ProcessLayerDataChangeEventHandler } from './handlers/process-layer-data-change-event.handler';
import { ProcessWorkflowChangeEventHandler } from './handlers/process-workflow-change-event.handler';
import { ProcessVmsDataChangeEventHandler } from './handlers/process-vms-data-change-event.handler';
import { MapPosition } from 'src/app/modules/cop/drawing/models/map-position.model';
import { ProcessTemporaryLayerTrackingRequest } from '../shared/process-temporary-layer-request.model';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MapDataWorkerService implements OnDestroy {
  worker: Worker;
  messageHandlerService: WebWorkerMessageHandlerService = new WebWorkerMessageHandlerService();
  destroy$: Subject<void> = new Subject();

  constructor(private store$: Store, private statsService: SignalRConnectionStatsService, private loggerService: LoggerService) {
    this.init();
  }

  init() {
    if (this.worker === undefined || typeof Worker !== 'undefined') {
      // Create a new worker
      const worker = new Worker(new URL('../worker/map-data.worker', import.meta.url), { type: 'module' });
      this.worker = worker;

      const environment = window['__env'];
      this.sendMessage(SET_ENVIRONMENT, { environment });

      this.worker.onmessage = message => {
        this.messageHandlerService.handleMessage(message.data.messageName, message.data.data);
      };

      // Subscribe to token changes
      this.store$.select(fromAuth.selectToken).pipe(takeUntil(this.destroy$)).subscribe(t => {
        if (!!t) {
          this.sendMessage(SET_AUTH_TOKEN, { token: t.access_token });
        }
      });
    }

    //
    // register message handlers here
    //
    this.messageHandlerService.registerHandler(
      PROCESS_LAYER_DATA_CHANGE_EVENTS,
      new ProcessLayerDataChangeEventHandler(this.store$, this.loggerService)
    );
    this.messageHandlerService.registerHandler(PROCESS_CONNECTION_STATS, new ConnectionStatsHandler(this.statsService, this.loggerService));
    this.messageHandlerService.registerHandler(
      PROCESS_DATASET_CHANGE_EVENT,
      new ProcessDatasetChangeEventHandler(this.store$, this.loggerService)
    );
    this.messageHandlerService.registerHandler(
      PROCESS_WORKFLOW_CHANGE_EVENT,
      new ProcessWorkflowChangeEventHandler(this.store$, this.loggerService)
    );
    this.messageHandlerService.registerHandler(
      PROCESS_VMS_DATA_CHANGE_EVENT,
      new ProcessVmsDataChangeEventHandler(this.store$, this.loggerService)
    );
  }

  populateAndTrackMap(request: PopulateAndTrackMapRequest): void {
    this.sendMessage(POPULATE_AND_TRACK_MAP, request);
  }

  populateAndTrackLayer(request: PopulateAndTrackLayerRequest): void {
    this.sendMessage(POPULATE_AND_TRACK_LAYER, request);
  }

  startTrackFlightLayerChanges(request: TrackFlightLayerChangesRequest): void {
    this.sendMessage(START_TRACK_FLIGHT_LAYER_CHANGES, request);
  }

  stopTrackFlightLayerChanges(request: TrackFlightLayerChangesRequest): void {
    this.sendMessage(STOP_TRACK_FLIGHT_LAYER_CHANGES, request);
  }

  processTemporaryLayerTracking(request: ProcessTemporaryLayerTrackingRequest): void {
    this.sendMessage(PROCESS_TEMPORARY_LAYER_TRACKING, request);
  }

  openVmsConnection(): void {
    this.sendMessage(OPEN_VMS_CONNECTION);
  }

  closeVmsConnection(): void {
    this.sendMessage(CLOSE_VMS_CONNECTION);
  }

  sendVmsRequest(integrationKey: string, cameraId: string, mediaWidths: number[], startTime: string, endTime: string): void {
    const request = {
      integrationKey,
      cameraId,
      mediaWidths,
      startTime,
      endTime
    };

    this.sendMessage(SEND_VMS_REQUEST, request);
  }

  sendMessage(messageName: string, data?: any): void {
    this.worker?.postMessage({ messageName, data });
  }

  setFloorFilterForLayers(floor: string): void {
    this.sendMessage(SET_FLOOR_FILTER_FOR_LAYERS, { floor });
  }

  setMapPositionAndZoom(mapPosition: MapPosition, visibleLayerUIds: string[]): void {
    this.sendMessage(SET_MAP_POSITION_AND_ZOOM, { mapPosition, visibleLayerUIds });
  }

  reset() {
    this.closeVmsConnection();
    setTimeout(() => {
      this.worker?.terminate();
      this.worker = undefined;
      this.init();
    }, 0);
  }

  async terminate(): Promise<void> {
    this.closeVmsConnection();
    await new Promise(resolve => setTimeout(resolve, 0));
    this.worker?.terminate();
    this.worker = undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.worker?.terminate();
  }
}
